import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * CameraButton — apre la fotocamera (webcam PC o cellulare) tramite getUserMedia
 * con preview live, scatto e conferma. Restituisce il File JPEG via onFile().
 *
 * Props:
 *  - onFile: (file: File) => void
 *  - label: stringa (default "Carica da fotocamera")
 *  - testid, className, disabled
 *  - variant: "outline" | "primary" | "ghost"
 *  - facingMode: "environment" (back) o "user" (front). Default "environment"
 */
export const CameraButton = ({
  onFile,
  label = "Carica da fotocamera",
  testid = "camera-btn",
  className = "",
  disabled = false,
  variant = "outline",
  facingMode = "environment",
}) => {
  const [open, setOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [captured, setCaptured] = useState(null);  // dataURL preview
  const [currentFacing, setCurrentFacing] = useState(facingMode);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Start stream when dialog opens
  useEffect(() => {
    let active = true;
    let localStream = null;
    if (!open) return undefined;
    setError(null);
    setCaptured(null);
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Il tuo browser non supporta l'accesso alla fotocamera. Usa Chrome / Edge / Safari aggiornato.");
        }
        localStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: currentFacing, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (!active) {
          localStream.getTracks().forEach((t) => t.stop());
          return;
        }
        setStream(localStream);
        if (videoRef.current) videoRef.current.srcObject = localStream;
      } catch (e) {
        const msg = e?.message || String(e);
        setError(
          msg.includes("Permission") || msg.includes("NotAllowed")
            ? "Permesso fotocamera negato. Abilita l'accesso nelle impostazioni del browser e riprova."
            : msg.includes("NotFound") || msg.includes("DevicesNotFound")
              ? "Nessuna fotocamera trovata su questo dispositivo."
              : `Impossibile accedere alla fotocamera: ${msg}`,
        );
      }
    })();
    return () => {
      active = false;
      if (localStream) localStream.getTracks().forEach((t) => t.stop());
    };
  }, [open, currentFacing]);

  // Cleanup on unmount/close
  useEffect(() => {
    if (!open && stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
  }, [open, stream]);

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    setCapturing(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setCaptured(dataUrl);
    setCapturing(false);
  };

  const retake = () => setCaptured(null);

  const confirm = () => {
    if (!captured) return;
    // dataURL → Blob → File
    const byteString = atob(captured.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const file = new File([ab], `foto-${ts}.jpg`, { type: "image/jpeg" });
    onFile?.(file);
    toast.success("Foto acquisita");
    setOpen(false);
  };

  const flipCamera = () => setCurrentFacing((f) => (f === "environment" ? "user" : "environment"));

  const styles = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white border-blue-600",
    outline: "border-amber-300 hover:bg-amber-100 text-amber-800 bg-white",
    ghost: "border-transparent hover:bg-amber-50 text-amber-800",
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant] || styles.outline} ${className}`}
        title="Apri la webcam del PC o la fotocamera del cellulare per acquisire un documento"
      >
        📷 {label}
      </button>

      <Dialog open={open} onOpenChange={(o) => !capturing && setOpen(o)}>
        <DialogContent className="max-w-2xl" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📷 Acquisisci da fotocamera
              <span className="text-xs font-normal text-slate-500">({currentFacing === "environment" ? "Retro" : "Fronte"})</span>
            </DialogTitle>
          </DialogHeader>

          {error && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              ❌ {error}
            </div>
          )}

          {!error && (
            <div className="space-y-3">
              <div className="relative aspect-video bg-black rounded-md overflow-hidden">
                {!captured ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-contain"
                    data-testid={`${testid}-video`}
                  />
                ) : (
                  <img src={captured} alt="Anteprima" className="w-full h-full object-contain" data-testid={`${testid}-preview`} />
                )}
                <canvas ref={canvasRef} className="hidden" />
              </div>

              <p className="text-xs text-slate-500 text-center">
                {!captured
                  ? "Inquadra il documento e premi “Scatta foto”. Su mobile puoi anche girare la fotocamera."
                  : "Controlla la foto. Se va bene conferma, altrimenti riprova."}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 flex-wrap">
            {!error && !captured && (
              <>
                <Button
                  variant="outline"
                  onClick={flipCamera}
                  disabled={!stream}
                  data-testid={`${testid}-flip`}
                  title="Cambia tra fotocamera frontale e posteriore"
                >
                  🔄 Gira
                </Button>
                <Button
                  onClick={capture}
                  disabled={!stream || capturing}
                  data-testid={`${testid}-capture`}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  📸 Scatta foto
                </Button>
              </>
            )}
            {!error && captured && (
              <>
                <Button variant="outline" onClick={retake} data-testid={`${testid}-retake`}>↩ Riprova</Button>
                <Button onClick={confirm} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`${testid}-confirm`}>
                  ✓ Usa questa foto
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid={`${testid}-cancel`}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CameraButton;
